import { InjectionToken } from '@angular/core';
import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * A terminal `portal_report_jobs` row, as far as the adapter reads one.
 *
 * A structural subset of the poller's own row type, not a second declaration of it: the
 * adapter looks at the outcome, the file it points at and the reason it gives, and at
 * nothing else the queue carries.
 */
export type FinishedReportJob = {
  readonly status: string;
  readonly storage_key: string | null;
  readonly error: string | null;
};

/**
 * How the adapter waits for a queued job to finish.
 *
 * The one implementation is `pollJob` in `portal/shared/utils/job-polling.util.ts`, shared
 * with three other job consumers and left exactly where it is. The domain layer may not
 * import it -- `portal-domain-no-portal-outside-domain` rejects the edge and the baseline
 * that grandfathers the old ones may not be extended (.claude/rules/architecture-records.md)
 * -- so the composition root hands the helper in rather than this adapter reaching up for
 * it. Nothing is forked and nothing is reimplemented; only the direction of the reference
 * changes.
 *
 * Its rejections travel to the caller untouched, so the two the helper raises by name
 * (aborted, timed out) stay distinguishable where the policy for them lives.
 */
export type ReportJobPoller = (
  supabase: SupabaseClient,
  jobId: string,
  intervalMs: number,
  timeoutMs: number,
  opts?: { readonly shouldStop?: () => boolean }
) => Promise<FinishedReportJob>;

export const REPORT_JOB_POLLER = new InjectionToken<ReportJobPoller>('REPORT_JOB_POLLER');
